import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { useAuthContext } from "./AuthContext";
import axios from "axios";
import moment from "moment";

export interface INotificationResponse {
    notification_expire_ts: number
    user_id: string
    notification_create_ts: number
    notification_title: string
    notification_sent_ts?: number
    notification_read_ts: number;
    notification_id: string
    notification_body: string
    notification_type: number
    notification_failed_ts: number;
    xpto?: number
}

export interface INotification {
    id: string;
    date: string;
    title: string;
    description: string;
    type: NotificationType;
}

export enum NotificationType {
    default = 0,
    success = 1,
    warning = 2,
    error = 3,
    info = 4
}

interface INotificationContext {
    notificationQuery: UseQueryResult<{
        [key: string]: INotification[];
    }|null, Error>;
}

interface INotificationProviderProps {
    children: React.ReactNode;
}

const NotificationContext = createContext<INotificationContext|null>(null);

const NotificationProvider = ({ children }: INotificationProviderProps) => {
    const authContext = useAuthContext();
    const notificationQuery = useQuery({
        queryKey: ["notifications", authContext.user.user_id],
        gcTime: 0,
        enabled: Boolean(authContext.isAuthenticated && authContext.user.user_id && authContext.token),
        queryFn: async () => {
            const data = (await axios.get<{ notifications: INotificationResponse[] }>(`${import.meta.env.VITE_API_URL}/user/${authContext.user.user_id}/notification`, {
                headers: {
                    Authorization: authContext.token
                }
            })).data;

            if (!data.notifications) {
                return null;
            }

            return data?.notifications?.reduce((acc: { [key: string]: INotification[] }, notification) => {
                const date = moment.unix(notification.notification_create_ts).format("YYYY-MM-DD");
                if (!acc[date]) {
                    acc[date] = [];
                }

                acc[date].push({
                    id: notification.notification_id,
                    date,
                    title: notification.notification_title,
                    description: notification.notification_body,
                    type: notification.notification_type
                });

                return acc;
            }, {});
        }
    });

    return (
        <NotificationContext.Provider value={{ notificationQuery }}>
            {children}
        </NotificationContext.Provider>
    );
}

const useNotificationContext = () => {
    const context = useContext(NotificationContext);

    if (!context)
        throw new Error(`NotificationContext cannot be null`);

    return context;
}

export { NotificationProvider, useNotificationContext }
